/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, UrlTree } from '@angular/router';
import { AppConfigService, AuthService, UserData } from '@epg/angular-base-lib';
import {
  ConfirmDeleteDialogData,
  ConfirmDialogService,
  EpgFrameMenuItem,
  TenantService
} from '@epg/ng-components';
import { AccountDto, TenantDto } from '@epg/tenant-manager-client';
import { Subscription } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { ApiService, COMPANY_NAME_QUERY_PARAM } from '../services/api.service';
import { FilterPersistenceService } from '../services/filter-persistence.service';
import { TenantManagerClientService } from '../services/tenant-manager-client.service';
import { TenantMappingService } from '../services/tenant-mapping.service';
import { QueryParamGuardService } from '../services/query-param-guard.service';
import { toTitleCase } from './shared/functions';

const TENANT_QUERY_PARAMETER_FORMAT = 'info';
const APPLICATION_NAME = 'oneapp';
const ELEARNING_SEGMENT = 'elearning';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'epgone';
  userData!: UserData;
  userAccount!: AccountDto;
  tenants_temp: TenantDto[] = [];
  activeTenantDto!: TenantDto | undefined;
  activeTenantName!: string | undefined;
  tenants!: TenantDto[];
  menuItems: EpgFrameMenuItem[] = [];
  routerEventsSubscription: Subscription = new Subscription();
  routerQueryParamsSubscription: Subscription = new Subscription();
  elearningEndPoint: string | undefined;
  hasTenantQueryParam: boolean = false;
  unauthorizedTenantMessage: string = '';

  @ViewChild('unauthorizedTenantTemplate', { static: true })
  unauthorizedTenantTemplate?: TemplateRef<any>;

  constructor(
    private authService: AuthService,
    private tenantManagerClientService: TenantManagerClientService,
    private tenantMappingService: TenantMappingService,
    private apiService: ApiService,
    private filterPersistenceService: FilterPersistenceService,
    public router: Router,
    private route: ActivatedRoute,
    protected appConfig: AppConfigService,
    private readonly dialogService: ConfirmDialogService,
    private queryParamGuard: QueryParamGuardService,
    private tenantService: TenantService
  ) {
    try {
      this.elearningEndPoint = this.appConfig.getParameter('ElearningEndpoint') as string;
      if (!this.elearningEndPoint.endsWith('/')) {
        this.elearningEndPoint = this.elearningEndPoint.concat('/');
      }
    } catch {
      this.elearningEndPoint = '';
    }
  }

  async ngOnInit(): Promise<void> {
    this.routerEventsSubscription = new Subscription();

    this.routerEventsSubscription.add(
      this.router.events
        ?.pipe(filter((e: any) => e instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
          this.preserveQueryParamsForSamePageNavigation(event.url);
        })
    );

    this.routerEventsSubscription.add(
      this.router.events
        ?.pipe(
          filter((e: any) => e instanceof NavigationEnd),
          pairwise()
        )
        .subscribe((e: NavigationEnd[]) => {
          let previousUrl = e[0].urlAfterRedirects;
          let currentUrl = e[1].urlAfterRedirects;
          if (this.isElearningUrl(currentUrl)) {
            this.router.navigateByUrl(previousUrl).then(() => {
              if (previousUrl.includes('/designer?')) {
                this.router.navigate([previousUrl]);
              }
              window.open(this.elearningEndPoint, '_blank', 'noreferrer');
            });
            return;
          }
        })
    );

    this.routerQueryParamsSubscription = this.route.queryParams.subscribe(async (params) => {
      const paramTenant = params['tenant'];
      if (paramTenant) {
        this.hasTenantQueryParam = true;
        if (!this.tenants) {
          this.tenants = await this.loadTenants();
        }
        this.activeTenantName = paramTenant;
        this.activeTenantDto = await this.getActiveTenantDto();
        this.tenantMappingService.setActiveTenant(this.activeTenantDto ?? null);
        if (
          paramTenant.toLowerCase() !==
          this.tenantMappingService.getActiveTenantFromSessionStorage()?.toLocaleLowerCase()
        ) {
          this.filterPersistenceService.clearAll();
          if (this.activeTenantDto) {
            this.tenantMappingService.setActiveTenantToSessionStorage(this.activeTenantDto.name);
            this.reloadPage();
          } else {
            await this.openDialog();
          }
        }
      }
    });

    this.authService.getUserInfo().then(async (userInfo) => {
      this.userData = userInfo;
      this.userAccount = { username: this.userData.name };
      sessionStorage.setItem('activeUser', this.userData.username);

      this.tenants = await this.loadTenants();

      if (this.hasTenantQueryParam) return;
      this.tenantMappingService.getActiveTenantName().then(async (tenantName) => {
        if (this.hasTenantQueryParam) return;

        this.activeTenantName = tenantName;
        this.activeTenantDto = await this.getActiveTenantDto();

        if (this.activeTenantDto) {
          this.tenantMappingService.setActiveTenant(this.activeTenantDto);
          this.tenantMappingService.setActiveTenantToSessionStorage(this.activeTenantDto.name);
          this.ensureTenantInUrl(this.activeTenantDto.name);
        } else if (this.tenants.length > 0) {
          this.activeTenantDto = this.tenants[0];
          this.tenantMappingService.setActiveTenant(this.activeTenantDto);
          this.tenantMappingService.setActiveTenantToSessionStorage(this.activeTenantDto.name);
          this.ensureTenantInUrl(this.activeTenantDto.name);
        } else {
          this.tenantMappingService.setActiveTenant(null);
          this.tenantMappingService.removeActiveTenantFromSessionStorage();
        }
      });
    });
  }
  private async loadTenants(): Promise<TenantDto[]> {
    const tenants = await this.getTenants();
    this.tenantService.setTenants(tenants);
    return tenants;
  }

  ngOnDestroy(): void {
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    if (this.routerQueryParamsSubscription) {
      this.routerQueryParamsSubscription.unsubscribe();
    }
  }

  private ensureTenantInUrl(tenantName: string): void {
    if (!tenantName?.trim()) {
      return;
    }

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const urlTree = this.router.parseUrl(currentUrl || this.router.url);
    if (urlTree.queryParams['tenant']) {
      return;
    }

    urlTree.queryParams['tenant'] = tenantName;
    void this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private isElearningUrl(url: string): boolean {
    return this.getPrimaryPath(this.router.parseUrl(url)) === ELEARNING_SEGMENT;
  }

  private getPrimaryPath(urlTree: UrlTree): string {
    return urlTree.root.children['primary']?.toString() ?? '';
  }

  private preserveQueryParamsForSamePageNavigation(targetUrl: string): void {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    const targetUrlTree = this.router.parseUrl(targetUrl);
    const currentTenant = currentUrlTree.queryParams['tenant'];

    if (!currentTenant) {
      return;
    }

    if (targetUrlTree.queryParams['tenant']) {
      return;
    }

    const currentPath = this.getPrimaryPath(currentUrlTree);
    const targetPath = this.getPrimaryPath(targetUrlTree);

    if (currentPath === targetPath && Object.keys(currentUrlTree.queryParams).length > 0) {
      // Same page: preserve all current query params
      void this.router.navigateByUrl(this.router.url, { replaceUrl: true });
    } else {
      // Different page: preserve tenant + params owned by surviving components
      const survivingParams = this.queryParamGuard.getParamsToPreserve(targetPath);
      targetUrlTree.queryParams = { ...survivingParams, tenant: currentTenant };
      void this.router.navigateByUrl(targetUrlTree, { replaceUrl: true });
    }
  }

  async getActiveTenantDto(): Promise<TenantDto | undefined> {
    return this.tenants.find(
      (tenant) => tenant.name.toLowerCase() === this.activeTenantName?.toLowerCase()
    );
  }

  async openDialog() {
    const title = $localize`:access denied dialog header@@accessDeniedHeader:Access Denied!`;
    this.unauthorizedTenantMessage = $localize`:no access to tenant message@@unauthorizedTenantMsg:You have tried to access the tenant <b>${this.activeTenantName}</b>, where you dont have access to. <br> Try logout, then click on the url again and login using an account where you have access to tenant <b>${this.activeTenantName}</b> <br> or stay in the current tenant and don't open the linked task.`;
    const confirmButtonText = $localize`:button signout@@signoutButton:Signout`;
    const cancelButtonText = $localize`:button text continue to default tenant@@buttonContinueToDefaultTenant:Stay in Tenant`;
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      title: title,
      message: '',
      template: this.unauthorizedTenantTemplate || undefined,
      confirmText: toTitleCase(confirmButtonText),
      cancelText: toTitleCase(cancelButtonText)
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe(async (result: boolean) => {
      if (result == true) {
        this.logout();
      } else {
        await this.router.navigateByUrl('home/workflow');
        this.reloadPage();
      }
    });
  }

  async getTenants(): Promise<TenantDto[]> {
    return await new Promise(async (resolve, reject) => {
      const tenants = await this.tenantManagerClientService.client
        .user()
        .applications(APPLICATION_NAME)
        .tenants()
        .get({ format: TENANT_QUERY_PARAMETER_FORMAT })
        .catch((error) => {
          reject(error);
        });
      let allowed_tenants: TenantDto[] = [];
      for await (const tenant of tenants!) {
        if (await this.hasWebAccessForTenant(tenant.name)) {
          allowed_tenants.push(tenant);
        }
      }
      resolve(allowed_tenants);
    });
  }

  async hasWebAccessForTenant(tenantName: string): Promise<boolean> {
    // return true;
    let permissions = await this.getPermissions(tenantName);
    if (permissions.length == 0) {
      // this(any) request will initialize the company in oneapp-api and create the permissions in tenantManager
      // if the user is a licensee and the company is not yet initialized in oneapp-api
      await this.tryToInitializeCompany(tenantName);
      permissions = await this.getPermissions(tenantName);
    }
    return permissions.some((permission: string) => permission === 'web:use');
  }

  tryToInitializeCompany(tenantName: string): Promise<string[]> {
    return new Promise(async (resolve, reject) => {
      return this.apiService
        .get('', new HttpParams().append(COMPANY_NAME_QUERY_PARAM, tenantName))
        .subscribe(
          (tenants: string[]) => {
            resolve(tenants);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }
  async getPermissions(tenantName: string) {
    return await this.tenantManagerClientService.client
      .user()
      .tenants(tenantName)
      .applications(APPLICATION_NAME)
      .permissions()
      .get();
  }

  async onActiveTenantChanged(tenant: TenantDto) {
    if (!this.activeTenantName) return;
    if (this.activeTenantName.toLowerCase() !== tenant.name.toLowerCase()) {
      this.filterPersistenceService.clearAll();
      this.activeTenantDto = tenant;
      this.activeTenantName = tenant.name;
      this.tenantMappingService.setActiveTenantToSessionStorage(tenant.name);
      const urlTree = this.router.createUrlTree(['home/workflow'], {
        queryParams: { tenant: tenant.name }
      });
      await this.router.navigateByUrl(urlTree);
      this.reloadPage();
    }
  }

  reloadPage() {
    window.location.reload();
  }

  logout(userName?: AccountDto) {
    this.tenantMappingService.removeActiveTenantFromSessionStorage();
    sessionStorage.removeItem('activeUser');
    this.authService.logout();
  }
}
